import { CalendarIcon, DollarSignIcon, InfoIcon } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Tooltip';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { formatCurrency } from 'utils/formatCurrency';
import { titleCase } from 'utils/formatStrings';

import { LTLRateMetadata } from '../LTLRateCardMetadata';
import { MetadataHintBadge } from './MetadataHintBadge';
import {
  RateMetadataTooltipContent,
  isTurvoRateMetadata,
} from './RateCardMetadata';

interface TurvoRateMetadataViewProps {
  rateMetadata: LTLRateMetadata;
  transitDays: number;
  externalId?: string;
}

export function TurvoRateMetadataView({
  rateMetadata,
  transitDays,
  externalId,
}: TurvoRateMetadataViewProps) {
  if (!isTurvoRateMetadata(rateMetadata)) {
    return null;
  }

  const displayTransitDays = rateMetadata.transitDays ?? transitDays;
  const metadataBadges = [];
  const serviceType = (rateMetadata.serviceType ?? '').trim();
  if (serviceType) {
    metadataBadges.push({
      label: titleCase(serviceType),
      tooltipDescription: 'Service type',
      icon: CalendarIcon,
    });
  }

  const pricingType = (rateMetadata.pricingType ?? '').trim();
  if (pricingType) {
    metadataBadges.push({
      label: titleCase(pricingType),
      tooltipDescription: 'Pricing type',
      icon: DollarSignIcon,
    });
  }

  return (
    <Flex direction='col' gap='sm'>
      <Flex direction='col' gap='xs'>
        <Flex gap='xs' align='center'>
          <Typography variant='body-sm' weight='semibold'>
            Rates:
          </Typography>
        </Flex>

        <Flex align='start' direction='col' gap='sm' className='min-w-0'>
          <Flex align='center' gap='xs' className='min-w-0 pl-2'>
            <Typography variant='body-sm' className='text-neutral shrink-0'>
              Payable:
            </Typography>
            <Typography variant='body-sm' className='text-success-600 truncate'>
              {formatCurrency(rateMetadata.payable ?? 0, 'USD', 2)}
            </Typography>
          </Flex>
          <Flex align='center' gap='xs' className='min-w-0 pl-2'>
            <Typography variant='body-sm' className='text-neutral shrink-0'>
              Billable:
            </Typography>
            <Typography variant='body-sm' className='text-success-600 truncate'>
              {formatCurrency(rateMetadata.billable ?? 0, 'USD', 2)}
            </Typography>
            <Tooltip delayDuration={10}>
              <TooltipTrigger
                asChild
                onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                  e.stopPropagation()
                }
              >
                <InfoIcon className='h-4 w-4 text-neutral-600 cursor-help shrink-0' />
              </TooltipTrigger>
              <TooltipContent
                className='bg-neutral-50 border-neutral-200'
                side='bottom'
              >
                <RateMetadataTooltipContent
                  rateMetadata={rateMetadata}
                  externalId={externalId}
                />
              </TooltipContent>
            </Tooltip>
          </Flex>
        </Flex>
      </Flex>

      <Flex align='center' gap='sm' className='w-full min-w-0'>
        <Typography variant='body-sm' weight='semibold'>
          Transit:
        </Typography>
        <Typography variant='body-sm' className='text-neutral-700 tabular-nums'>
          {displayTransitDays} day{displayTransitDays !== 1 ? 's' : ''}
        </Typography>
      </Flex>
      {metadataBadges.length > 0 && (
        <Flex direction='row' gap='xs' className='w-full min-w-0'>
          {metadataBadges.map(({ label, tooltipDescription, icon }) => (
            <MetadataHintBadge
              key={`${tooltipDescription}-${label}`}
              label={label}
              tooltipDescription={tooltipDescription}
              icon={icon}
            />
          ))}
        </Flex>
      )}
    </Flex>
  );
}
